/**
 * getBuildId — the single read point for the build identifier injected by
 * this project's two deploy paths.
 *
 * feedback-implementation/TS-003 (T3, FR-1.9): before this story
 * VITE_APP_BUILD_ID was undefined in uat and prod, so errorReporter.ts's old
 * `?? 'unknown'` read always fell back safely (`??` coalesces null and
 * undefined, but NOT an empty string). TS-003/T1 makes CI define the
 * variable, which makes an empty or malformed value newly reachable — a
 * mis-typed future expression, a workflow refactor, or a developer's local
 * .env containing a bare `VITE_APP_BUILD_ID=` copied from .env.example.
 * Without this helper, any of those would silently store an empty-string
 * appVersion, which reads in triage as "the field is broken" rather than
 * "this build is unidentified" — the exact failure mode TO-LL-036 warns
 * against (a silent fallback that quietly negates the feature).
 *
 * The value is a 40-character full ${{ github.sha }} in uat/prod
 * (.github/workflows/deploy-frontend.yml) and a 7-character short sha on dev
 * (scripts/deploy-dev-frontend.sh:88, `git rev-parse --short HEAD`, falling
 * back to 'dev-local'). This divergence is DELIBERATE (TS-003 edge case 4,
 * decision DD-1) — callers must never assume a fixed length or parse this
 * value.
 *
 * US-008 (the feedback sheet) is expected to call getBuildId() for
 * context.appVersion rather than reading import.meta.env directly, so both
 * consumers share one tested normalisation.
 */

/** The literal fallback value for a missing, empty, or whitespace-only build id. */
export const UNKNOWN_BUILD_ID = 'unknown';

/**
 * IC-001 caps context.appVersion at 64 characters. A real sha (max 40 chars)
 * never hits this cap in practice; it exists so a future, longer identifier
 * format degrades to a truncated real value rather than a server-rejected
 * over-length field.
 */
const MAX_BUILD_ID_LENGTH = 64;

/**
 * Read import.meta.env.VITE_APP_BUILD_ID and return a safe value: the
 * trimmed string (capped at 64 chars) when one is present, or the literal
 * 'unknown' when the variable is missing, not a string, empty, or
 * whitespace-only. Total function — never throws, never logs, no I/O.
 *
 * Read at CALL time (not module scope) so a per-test env stub
 * (vi.stubEnv) is actually observed.
 */
export function getBuildId(): string {
  const raw: unknown = import.meta.env.VITE_APP_BUILD_ID;

  if (typeof raw !== 'string') {
    return UNKNOWN_BUILD_ID;
  }

  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return UNKNOWN_BUILD_ID;
  }

  return trimmed.slice(0, MAX_BUILD_ID_LENGTH);
}
